const _excluded = ["setEnabled"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getAdminConstructionApiUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext, useContextWithValidation } from '@adsk/alloy-react-context';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import { useDeletedUsers } from './DeletedUsers';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useClientSideDataModification from './hooks/useClientSideDataModification';
/**
 * Context for users list.
 */
const UsersContext = getOrCreateGlobalContext(CONTEXT_NAMES.UsersContext);
const useUsersContext = () => useContextWithValidation(UsersContext, {
  hook: 'useUsers',
  provider: 'UsersProvider'
});
export const useUsers = () => {
  const _useUsersContext = useUsersContext(),
    {
      setEnabled
    } = _useUsersContext,
    rest = _objectWithoutProperties(_useUsersContext, _excluded);
  useEffect(() => {
    if (setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled]);
  return rest;
};
const useFindUserBy = function (matcher) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    includeDeleted
  } = options;
  const {
    data: users,
    loading: areUsersLoading,
    error: usersError
  } = useUsers();
  const {
    data: deletedUsers,
    loading: areDeletedUsersLoading,
    error: deletedUsersError
  } = useDeletedUsers({
    disable: !includeDeleted
  });
  const activeUser = useMemo(() => users.find(matcher), [users, matcher]);
  const shouldSearchDeletedUser = !activeUser && includeDeleted;
  const deletedUser = useMemo(() => shouldSearchDeletedUser ? deletedUsers.find(matcher) : undefined, [shouldSearchDeletedUser, deletedUsers, matcher]);
  const user = activeUser || deletedUser;
  return {
    user,
    loading: !user && (areUsersLoading || includeDeleted && areDeletedUsersLoading),
    error: !user && (usersError || includeDeleted && deletedUsersError)
  };
};
export const useUserById = function (id) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const matcher = useCallback(user => user.id === id, [id]);
  return useFindUserBy(matcher, options);
};
export const useUserByAutodeskId = function (autodeskId) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const matcher = useCallback(user => user.autodeskId === autodeskId, [autodeskId]);
  return useFindUserBy(matcher, options);
};
const PAGE_LIMIT = 200;
const handlePendingUsers = users => users.map(user => _objectSpread(_objectSpread({}, user), {}, {
  name: user.status === 'pending' || user.name === 'New Member' ? user.email : user.name
}));
export const UsersProvider = _ref => {
  let {
    children
  } = _ref;
  const [enabled, setEnabled] = useState(false);
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminConstructionApiUrl();
  const url = enabled && projectId ? "".concat(adminApiUrl, "/v1/projects/").concat(projectId, "/users?limit=").concat(PAGE_LIMIT) : '';
  const {
    loading: loadingData,
    error,
    data: dataFromBE,
    retry,
    clearSessionStorage
  } = useGetAllPaginatedData({
    url
  });
  const usersFromBE = useMemo(() => {
    return dataFromBE && removeOldDuplicatesBy(dataFromBE.flatMap(usersPages => handlePendingUsers(usersPages.results)), item => item.id);
  }, [dataFromBE]);
  const {
    modifyData,
    addData: addUsers,
    updateEntity: updateUser,
    removeEntityById: removeUserById
  } = useClientSideDataModification(clearSessionStorage);
  const loading = loadingData || !enabled;
  const data = useMemo(() => modifyData(usersFromBE), [modifyData, usersFromBE]);
  const removeUserByAutodeskId = useCallback(autodeskId => {
    const user = data.find(user => user.autodeskId === autodeskId);
    if (user) {
      removeUserById(user.id);
    }
  }, [data, removeUserById]);
  const value = useMemo(() => ({
    data,
    loading,
    error,
    retry,
    addUsers,
    updateUser,
    removeUserById,
    removeUserByAutodeskId,
    setEnabled: enabled ? undefined : setEnabled
  }), [enabled, addUsers, updateUser, data, error, loading, removeUserById, removeUserByAutodeskId, retry]);
  return /*#__PURE__*/React.createElement(UsersContext.Provider, {
    value: value
  }, children);
};
UsersProvider.displayName = 'UsersProvider';
UsersProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node
};
//# sourceMappingURL=UsersContext.js.map